import React, { useState, useCallback, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";

import { ViewIcon } from "../../../assets";
import { EXPORT_TYPE, STATUS_LABEL } from "../../../system/global-enums";
import {
  EMPTY_STRING,
  SCREEN_PATH,
  PAYLOAD_KEYS,
  adminReduxConstants,
  customerExportHeaders,
  CONFIRMATION_MESSAGE,
} from "../../../constants";
import {
  fileName,
  getSortBy,
  getPageNumber,
  buildFilterQuery,
  customStringSorting,
} from "../../../shared/utility";
import ReactTable from "../../../shared/components/tables/ReactTable";
import SearchField from "../../../shared/components/search/SearchField";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import ExportDropdown from "../../../shared/components/export/ExportDropdown";
import ConfirmationModal from "../../../shared/components/modals/ConfirmationModal";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions, systemActions } from "../../../redux/actions";

const { FILTER_QUERY, PAGE_SIZE, PAGE_NUMBER, SEARCH } = PAYLOAD_KEYS;
const { GET_CUSTOMERS, CUSTOMERS_PAGINATION, GET_CUSTOMERS_EXPORT_ALL_DATA } =
  adminReduxConstants;

const Customers = ({
  customers,
  spinnerActions,
  updatePagination,
  customersPagination,
  customerStatusUpdate,
  customersAllItemsForExport,
  getCustomers,
  resetAllExportItems,
  getAllCustomersItemsForExport,
  setSelectedRowId,
  selectedRowId,
}) => {
  const csvLinkRef = useRef();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper();
  const [exportData, setExportData] = useState([]);
  const [sorting, setSorting] = useState({ id: "id", desc: true });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const columns = [
    columnHelper.accessor("id", {
      header: <div className="column-head">Customer Id</div>,
      cell: ({ row: { original } }) => <div>{original.id}</div>,
      enableSorting: true,
    }),
    columnHelper.accessor("name", {
      header: <div className="column-head">Name</div>,
      cell: ({ row: { original } }) => (
        <div title={original.name} className="line-clamp">
          {original.name}
        </div>
      ),
      enableSorting: true,
      sortingFn: customStringSorting,
      size: 150,
    }),
    columnHelper.accessor("email", {
      header: <div className="column-head">Email</div>,
      cell: ({ row: { original } }) => (
        <div title={original.email} className="line-clamp">
          {original.email}
        </div>
      ),
      enableSorting: true,
      sortingFn: customStringSorting,
      size: 150,
    }),
    columnHelper.accessor("defaultCurrency", {
      header: <div className="column-head">Currency</div>,
      cell: ({ row: { original } }) => <div>{original.defaultCurrency}</div>,
      enableSorting: true,
      sortingFn: customStringSorting,
    }),
    columnHelper.accessor("status", {
      header: <div className="column-head">Status</div>,
      cell: ({ row: { original } }) => (
        <div className="admin-switch-status">
          <div class="form-check form-switch">
            <input
              class="form-check-input"
              type="checkbox"
              role="switch"
              checked={original.status}
              onChange={() => {
                setSelectedRowId(original.id);
                setShowConfirmationModal(true);
              }}
            />
          </div>
        </div>
      ),
      size: 100,
      enableSorting: true,
    }),
    columnHelper.accessor("action", {
      header: <div className="column-head text-end">Action</div>,
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <div className="text-end pe-3">
          <img
            className="pointer"
            src={ViewIcon}
            onClick={() =>
              navigate(`${SCREEN_PATH.ADMIN_CUSTOMERS}/${original.id}`)
            }
            alt={EMPTY_STRING}
          />
        </div>
      ),
      size: 100,
    }),
  ];

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const onConfirmation = () => {
    setShowConfirmationModal(false);
    customerStatusUpdate(selectedRowId, () =>
      getCustomers({ ...customersPagination, orderBy: getSortBy(sorting) })
    );
  };

  // Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 1000);
  };

  const updatedRecord = (items) => {
    return items.map((item) => {
      return {
        ...item,
        status: item.status ? STATUS_LABEL.ACTIVE : STATUS_LABEL.INACTIVE,
      };
    });
  };

  // Get data for export
  const getExportData = (type) => {
    if (type === EXPORT_TYPE.GRID) {
      setExportData(updatedRecord(customers));
      exportCSV();
    }
    if (type === EXPORT_TYPE.ALL) {
      const updatedPayload = {
        ...customersPagination,
        [PAGE_NUMBER]: 0,
        [PAGE_SIZE]: customersPagination.total,
        [FILTER_QUERY]: getFilterQuery(customersPagination.search),
        orderBy: getSortBy(sorting),
      };
      getAllCustomersItemsForExport(updatedPayload);
    }
  };

  const getFilterQuery = (data) => {
    const searchFields = ["UserId", "Name", "Email"];
    return buildFilterQuery(data, searchFields);
  };

  const handleUpdatePagination = (data, type) => {
    const payload = {
      ...customersPagination,
      [SEARCH]: type === SEARCH ? data : customersPagination.search,
      [PAGE_SIZE]: type === PAGE_SIZE ? data : customersPagination.pageSize,
      [PAGE_NUMBER]: getPageNumber(type, data, customersPagination),
    };

    updatePagination(payload, CUSTOMERS_PAGINATION);
  };

  useEffect(() => {
    return () => {
      updatePagination(
        {
          [PAGE_SIZE]: 10,
          [PAGE_NUMBER]: 0,
          [SEARCH]: EMPTY_STRING,
          [FILTER_QUERY]: EMPTY_STRING,
        },
        CUSTOMERS_PAGINATION
      );
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const updatedPayload = {
      ...customersPagination,
      [FILTER_QUERY]: getFilterQuery(customersPagination.search),
      orderBy: getSortBy(sorting),
    };
    getCustomers(updatedPayload);
    // eslint-disable-next-line
  }, [
    customersPagination.search,
    customersPagination.pageNumber,
    customersPagination.pageSize,
    sorting,
  ]);

  useEffect(() => {
    if (customersAllItemsForExport.length) {
      setExportData(updatedRecord(customersAllItemsForExport));
      exportCSV();
      resetAllExportItems(GET_CUSTOMERS_EXPORT_ALL_DATA);
    }
    // eslint-disable-next-line
  }, [customersAllItemsForExport]);

  return (
    <MainContentWrapper>
      {spinner(GET_CUSTOMERS) > -1 && <MainSpinner className="admin-spinner" />}
      <ConfirmationModal
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => onConfirmation()}
        show={showConfirmationModal}
        message={CONFIRMATION_MESSAGE}
      />
      <div className="admin-page-heading">Customers</div>
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between">
        <SearchField
          className="d-inline-block"
          value={customersPagination.search}
          handleSearch={(val) => handleUpdatePagination(val, SEARCH)}
        />
        <ExportDropdown className="mt-3 mt-sm-4" onExport={getExportData} />
      </div>
      <ReactTable
        data={customers}
        columns={columns}
        paginationData={{
          currentPage: customersPagination.pageNumber,
          totalItems: customersPagination?.total ?? 0,
          pageSize: customersPagination.pageSize,
          onPageChange: (page) => handleUpdatePagination(page, PAGE_NUMBER),
          onPageSizeChange: (pageSize) =>
            handleUpdatePagination(pageSize, PAGE_SIZE),
        }}
        sortedBy={sorting}
        onColumnSort={(sortBy) => setSorting(sortBy)}
      />
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={customerExportHeaders}
        data={exportData}
        filename={fileName("Customers")}
      />
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  customers: state.adminReducer.customers,
  spinnerActions: state.systemReducer.spinnerActions,
  customersPagination: state.adminReducer.customersPagination,
  customersAllItemsForExport: state.adminReducer.customersAllItemsForExport,
  selectedRowId: state.systemReducer.selectedRowId,
});

const mapDispatchToProps = (dispatch) => ({
  getCustomers: (data) => dispatch(adminActions.getCustomers(data)),
  getAllCustomersItemsForExport: (data) =>
    dispatch(adminActions.getAllCustomersItemsForExport(data)),
  customerStatusUpdate: (id, func) =>
    dispatch(adminActions.customerStatusUpdate(id, func)),
  updatePagination: (data, type) =>
    dispatch(adminActions.updatePagination(data, type)),
  resetAllExportItems: (type) =>
    dispatch(adminActions.resetAllExportItems(type)),
  setSelectedRowId: (id) => dispatch(systemActions.setSelectedRowId(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Customers);
